import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useState } from "react";
import Layout from "../../components/layout";
import Link from "../../components/Link";
import Card from "../../components/v2/cards/card";
import PlatformHeroSection from "../../components/v2/common/platform-hero";
import { Collaps, Expand, MwSmallIcon } from "../../components/v2/icons";
import ProductResources from "../../components/v2/product/product-resource";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/platform.module.scss";

const Platform = () => {
  const { t } = useTranslation("common");
  const tabsRaw = t('platform.main.tabs', { returnObjects: true }) as any
  const tabContents = Array.isArray(tabsRaw) ? tabsRaw : []
  const [activeTab, setActiveTab] = useState(tabContents[0]?.title || "Unified Experience");

  const handleTabClick = (title) => {
    setActiveTab(title);
  };

  const hero = t('platform.main.hero', { returnObjects: true }) as any
  const allFeatures = t('platform.main.allFeatures', { returnObjects: true }) as any

  const benefits = t('platform.main.benefits', { returnObjects: true }) as any
  const platformCards = (benefits?.cards && Array.isArray(benefits.cards)) ? benefits.cards : []

  const resourcesRaw = t('platform.main.resources', { returnObjects: true }) as any
  const resources = Array.isArray(resourcesRaw) ? resourcesRaw : []

  return (
    <>
      <>
        <Layout
          pageTitle={"Unified Observability Platform | Middleware"}
          uri={"/platform/"}
          metaTitle={"Unified Observability Platform | Middleware"}
          metaDesc={
            "Unify and correlate telemetry data in real-time with Middleware. Get a clear view of logs, metrics, and more to inform decision-making and boost performance."
          }
          ogImage={""}
          metaRobotsNoindex={"index"}
          metaRobotsNofollow={"follow"}
          isHomePage={false}
        >
          <div className={"platform-main"}>
          <PlatformHeroSection
              title={
                <h1>
                  {hero?.titlePrefix} <span className={"text-gradient"}>{hero?.titleHighlight}</span>{hero?.titleSuffix?.includes("\\n") ? <><br/> {hero?.titleSuffix?.replace("\\n", " ")}</> : <> {hero?.titleSuffix}</>}
                </h1>
              }
            img={hero?.image}
            imgAlt={hero?.imageAlt}
              className={"platform-page"}
            />

            <section className={"platform-features"}>
              <div className={"container"}>
                <div className={"section-tag"}>
                  <MwSmallIcon />
                  {allFeatures?.sectionTag}
                </div>
                <h2>
                  <span className={"text-gradient"}>{allFeatures?.titlePrefix}</span> {allFeatures?.titleSuffix}
                </h2>
                <div className={"d-flex"}>
                  <div className={"left-block"}>
                    {tabContents.map((tab, index) => (
                      <div
                        key={index}
                        className={`tabs-platform ${
                          activeTab === tab.title ? "active" : ""
                        }`}
                      >
                        <div
                          className={"title-flex"}
                          onClick={() => handleTabClick(tab.title)}
                        >
                          <button
                            className={activeTab === tab.title ? "active" : ""}
                          >
                            {tab.title}
                          </button>
                          {activeTab === tab.title ? <Collaps /> : <Expand />}
                        </div>
                        <div
                          className={"content"}
                          style={{
                            height: activeTab === tab.title ? "auto" : 0,
                            overflow: "hidden",
                            opacity: activeTab === tab.title ? 1 : 0,
                            transition: "height 0.5s ease, opacity 0.5s ease",
                          }}
                        >
                          <p>{tab.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={"right-block"}>
                    <div className={"platform-inner-img"}>
                      {tabContents.map((tab, index) => (
                        <>
                          {" "}
                          <img
                            key={index}
                            src={tab.imgSrc}
                            alt={tab.title}
                            width={100}
                            height={100}
                            style={{
                              display:
                                activeTab === tab.title ? "block" : "none",
                              opacity: activeTab === tab.title ? 1 : 0,
                              transition: "opacity 0.5s ease",
                            }}
                          />
                          <Link
                            href={tab.link}
                            className={"unified-img-link read-more"}
                            style={{
                              display:
                                activeTab === tab.title ? "block" : "none",
                              opacity: activeTab === tab.title ? 1 : 0,
                              transition: "opacity 0.5s ease",
                            }}
                          >
                            <p>{tab.ctaLabel}</p>
                          </Link>
                        </>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className={"mw-card career"}>
              <div className={"container"}>
                <div className={"d-flex card-items"}>
                  <div className={"card-item"}>
                    <div className={"section-tag testimonial pd-right"}>
                      <MwSmallIcon />
                      Benefits
                    </div>
                    <h2>
                      Streamline Operations from{" "}
                      <span className={"text-gradient"}> End-to-End</span>
                    </h2>
                  </div>
                  {platformCards.map((item, index) => {
                    return (
                      <Card
                        key={index}
                        heading={item.heading}
                        description={item.description}
                        isHeading={item.isHeading}
                      />
                    );
                  })}
                </div>
              </div>
            </section>

            <ProductResources
              resources={resources}
              title={t('platform.shared.resourcesTitle')}
            />
          </div>
        </Layout>
      </>
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      pageTitle: "Unified Observability Platform | Middleware",
      description: "Unify and correlate telemetry data in real-time with Middleware. Get a clear view of logs, metrics, and more to inform decision-making and boost performance.",
      ogImage: "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
      uri: "/platform/",
      metaTitle: "Unified Observability Platform | Middleware",
      metaDesc: "Unify and correlate telemetry data in real-time with Middleware. Get a clear view of logs, metrics, and more to inform decision-making and boost performance.",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
      ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer'], nextI18NextConfig)),
    },
  }
}

export default Platform;
